import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Play, Share2, Trophy, Trash2, FolderPlus, FolderOpen, Download } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api, apiError } from '@/lib/api';
import { shareSong } from '@/lib/share';
import { usePlayer } from '@/context/PlayerContext';
import { useAuth } from '@/context/AuthContext';
import { Paywall } from '@/components/Paywall';

export function SongActionsMenu({ song, onDeleted, existingProjectId, onProjectCreated }) {
  const { playTrack } = usePlayer();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const isPremium = !!profile?.is_premium;
  const [participateOpen, setParticipateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [paywall, setPaywall] = useState(null); // feature label or null
  const canPlay = song.generation_status === 'completed' && song.audio_url;
  const isPrivate = song.visibility === 'private';

  const track = {
    song_id: song.id, title: song.title, slug: song.slug, cover_url: song.cover_url,
    audio_url: song.audio_url, style_text: song.style_text, duration_seconds: song.duration_seconds,
  };

  const doDownload = async () => {
    if (!isPremium) { setPaywall('Le téléchargement'); return; }
    try {
      const { data } = await api.get(`/songs/${song.id}/download`);
      const a = document.createElement('a');
      a.href = data.download_url;
      a.download = data.filename || 'mymusic.mp3';
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Téléchargement lancé');
    } catch (e) {
      if (e?.response?.status === 402) setPaywall('Le téléchargement');
      else toast.error(apiError(e));
    }
  };

  const onShare = () => {
    if (!isPremium) { setPaywall('Le partage'); return; }
    shareSong({ slug: song.slug, title: song.title });
  };

  const onParticipate = () => {
    if (!isPremium) { setPaywall('La participation aux challenges'); return; }
    setParticipateOpen(true);
  };

  const openInStudio = async () => {
    if (existingProjectId) { navigate(`/studio/${existingProjectId}`); return; }
    try {
      const { data } = await api.post('/projects/from-song', { song_id: song.id });
      if (data.already_existed) toast.info('Projet existant — ouverture.');
      else toast.success('Projet Studio créé');
      onProjectCreated?.();
      navigate(`/studio/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/songs/${song.id}`);
      toast.success('Morceau supprimé');
      onDeleted?.(song.id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-surface-cardHover hover:text-white"
                  data-testid={`song-menu-${song.id}`}>
            <MoreVertical className="h-5 w-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="glass w-52">
          {canPlay && (
            <DropdownMenuItem onClick={() => playTrack(track, [track])} data-testid={`menu-play-${song.id}`}>
              <Play className="mr-2 h-4 w-4" /> Écouter
            </DropdownMenuItem>
          )}
          {isPrivate && canPlay && (
            <DropdownMenuItem onClick={doDownload} data-testid={`menu-download-${song.id}`}>
              <Download className="mr-2 h-4 w-4" /> Télécharger
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={onShare}
                            disabled={song.visibility !== 'public'} data-testid={`menu-share-${song.id}`}>
            <Share2 className="mr-2 h-4 w-4" /> Partager (WhatsApp)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={openInStudio} data-testid={`menu-studio-${song.id}`}>
            {existingProjectId
              ? <><FolderOpen className="mr-2 h-4 w-4" /> Ouvrir le projet</>
              : <><FolderPlus className="mr-2 h-4 w-4" /> Ouvrir dans Studio</>}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onParticipate} disabled={!canPlay}
                            data-testid={`menu-participate-${song.id}`}>
            <Trophy className="mr-2 h-4 w-4" /> Participer à un Challenge
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setConfirmDelete(true)} className="text-destructive focus:text-destructive"
                            data-testid={`menu-delete-${song.id}`}>
            <Trash2 className="mr-2 h-4 w-4" /> Supprimer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Paywall open={!!paywall} onOpenChange={(o) => !o && setPaywall(null)} feature={paywall || ''} />
      <ParticipateDialog song={song} open={participateOpen} onOpenChange={setParticipateOpen} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="glass">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce morceau ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. Le morceau et ses participations seront supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function ParticipateDialog({ song, open, onOpenChange }) {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(null);
    api.get(`/songs/${song.id}/eligible-challenges`)
      .then(({ data }) => setChallenges(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [open, song.id]);

  const submit = async () => {
    try {
      await api.post('/challenges/participate', { song_id: song.id, challenge_id: selected });
      toast.success('Participation enregistrée ! Ton morceau est désormais public dans ce challenge.');
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass" data-testid="participate-dialog">
        <DialogHeader>
          <DialogTitle>Participer à un Challenge</DialogTitle>
          <DialogDescription>Choisis un challenge actif compatible avec « {song.title} ».</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {loading && <p className="py-6 text-center text-sm text-muted-foreground">Chargement...</p>}
          {!loading && challenges.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">Aucun challenge actif pour le moment.</p>
          )}
          {challenges.map((c) => {
            const disabled = c.already_entered || !c.compatible;
            return (
              <button key={c.id} disabled={disabled} onClick={() => setSelected(c.id)}
                      data-testid={`participate-option-${c.slug}`}
                      className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition-all ${
                        selected === c.id ? 'border-brand bg-brand/10' : 'border-border bg-surface-card'
                      } ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-brand/50'}`}>
                <div>
                  <p className="font-semibold text-white">{c.emoji} {c.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {c.already_entered ? 'Déjà inscrit' : !c.compatible ? 'Style non compatible' : c.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={!selected}
                  className="w-full rounded-full bg-brand py-2.5 font-semibold text-black btn-glow hover:bg-brand-hover disabled:opacity-40"
                  data-testid="confirm-participate">
            Confirmer la participation
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
